import { ChevronRight, Ellipsis, Package, Pencil, Trash } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Money, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { Product } from '@/interfaces/catalog/Product'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatCount, ProductSummary, productTypeLabel } from '@/lib/catalog/catalogTypes'
import { cn } from '@/lib/utils'
import { formatQuantity, LOW_STOCK_THRESHOLD } from '@/lib/warehouse/stockLevels'

interface ProductItemProps {
  product?: Product
  summary?: ProductSummary
  categoryName?: string
  categoryPath?: string
  showStock?: boolean
  canEdit?: boolean
  onDelete?: () => void
}

export const ProductThumb = ({ src, className }: { src?: string; className?: string }) => (
  <span
    className={cn(
      'flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted text-muted-foreground',
      className,
    )}
  >
    {src ? <img src={src} alt="" className="size-full object-cover" /> : <Package className="size-4" />}
  </span>
)

const StockCell = ({ summary }: { summary?: ProductSummary }) => {
  const stock = summary?.stock
  if (!stock) return <span className="text-muted-foreground">-</span>
  if (!stock.tracked) return <span className="text-muted-foreground">Neomezeno</span>
  const out = stock.available <= 0
  const low = !out && stock.available <= LOW_STOCK_THRESHOLD
  return (
    <div>
      <p className={cn(out && 'text-danger', low && 'text-warning')}>
        {out ? 'Vyprodáno' : `${formatQuantity(stock.available)} ks`}
      </p>
      {summary.variantCount > 1 && (
        <p className="text-xs text-muted-foreground">{formatCount(summary.variantCount, 'varianta', 'varianty', 'variant')}</p>
      )}
    </div>
  )
}

export const PriceRange = ({ summary }: { summary?: ProductSummary }) => {
  if (!summary || summary.minPrice === null) return <span className="text-muted-foreground">-</span>
  if (summary.minPrice === summary.maxPrice) return <Money amount={summary.minPrice} currency={summary.currency} />
  return (
    <span className="inline-flex items-baseline">
      <Money amount={summary.minPrice} currency={summary.currency} />
      <span className="px-1 text-muted-foreground">-</span>
      <Money amount={summary.maxPrice} currency={summary.currency} />
    </span>
  )
}

export const ProductItem = ({
  product,
  summary,
  categoryName,
  categoryPath,
  showStock,
  canEdit = true,
  onDelete,
}: ProductItemProps) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  if (!product) {
    return (
      <TableRow className="hover:bg-transparent">
        <TableCell>
          <div className="flex items-center gap-3">
            <Skeleton className="size-9" />
            <Skeleton className="h-4 w-32 md:w-48" />
          </div>
        </TableCell>
        {Array.from({ length: showStock ? 5 : 4 }).map((_, i) => (
          <TableCell key={i} className={cn(i !== 2 && 'hidden md:table-cell')}>
            <Skeleton className="ml-auto h-4 w-full max-w-20 md:ml-0" />
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    )
  }

  const handleDelete = async (): Promise<void> => {
    try {
      await API.delete(`/api/v2/products/${product.id}`)
      toast.success('Produkt byl smazán')
      onDelete?.()
    } catch (error: unknown) {
      console.error('Delete error:', error)
      toast.error('Produkt se nepodařilo smazat', {
        description: extractErrorMessage(error) || 'Došlo k neočekávané chybě',
      })
      throw error
    }
  }

  const variants = summary?.variantCount ?? 0
  const typeLabel = productTypeLabel[product.productType ?? 'physical'] ?? product.productType
  const stock = summary?.stock
  const mobileStock =
    stock && stock.tracked
      ? {
          out: stock.available <= 0,
          low: stock.available > 0 && stock.available <= LOW_STOCK_THRESHOLD,
          label: stock.available <= 0 ? 'Vyprodáno' : `${formatQuantity(stock.available)} ks`,
        }
      : null

  return (
    <>
      <TableRow className="group cursor-pointer" onClick={() => navigate(`/catalog/product/${product.id}`)}>
        <TableCell className="max-w-0 w-full">
          <div className="flex items-center gap-3 md:min-w-52">
            <ProductThumb src={summary?.thumbnail} />
            <div className="min-w-0">
              <p className="truncate font-medium">{product.name}</p>
              <p className="hidden truncate text-xs text-muted-foreground md:block">
                {typeLabel}
              </p>
              <p className="truncate text-xs text-muted-foreground md:hidden">
                {[
                  categoryName ?? typeLabel,
                  variants > 1 ? formatCount(variants, 'varianta', 'varianty', 'variant') : null,
                  product.isActive === false ? 'Neaktivní' : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                {showStock && mobileStock && (
                  <>
                    {', '}
                    <span className={cn(mobileStock.out && 'text-danger', mobileStock.low && 'text-warning')}>
                      {mobileStock.label}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className="hidden max-w-48 md:table-cell">
          {categoryName ? (
            <p className="truncate" title={categoryPath}>
              {categoryName}
            </p>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="hidden text-right md:table-cell">
          {variants > 0 ? formatQuantity(variants) : <span className="text-muted-foreground">-</span>}
        </TableCell>
        <TableCell className="text-right">
          <span className="hidden md:inline">
            <PriceRange summary={summary} />
          </span>
          <span className="md:hidden">
            {summary && summary.minPrice !== null && summary.minPrice !== summary.maxPrice ? (
              <span className="inline-flex items-baseline gap-1">
                <span className="text-xs text-muted-foreground">od</span>
                <Money amount={summary.minPrice} currency={summary.currency} />
              </span>
            ) : (
              <PriceRange summary={summary} />
            )}
          </span>
        </TableCell>
        {showStock && (
          <TableCell className="hidden text-right md:table-cell">
            <StockCell summary={summary} />
          </TableCell>
        )}
        <TableCell className="hidden md:table-cell">
          {product.isActive !== false ? (
            <StatusBadge status="paid" label="Aktivní" />
          ) : (
            <StatusBadge status="draft" label="Neaktivní" />
          )}
        </TableCell>
        <TableCell className="w-px pl-0 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Akce">
                    <Ellipsis />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/catalog/product/${product.id}/edit`)}>
                    <Pencil />
                    Upravit
                  </DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onClick={() => setIsOpen(true)}>
                    <Trash />
                    Smazat
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ChevronRight className="hidden size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block" />
          </div>
        </TableCell>
      </TableRow>
      <ConfirmDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={`Smazat produkt ${product.name}?`}
        description="Tato akce je nevratná. Opravdu chcete smazat tento produkt?"
        onConfirm={handleDelete}
      />
    </>
  )
}
